import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, gql, useQuery } from "@apollo/client";
import { Select, SelectItem, TextInput } from "@carbon/react";
import TeacherSelector from "../../../Components/teacherSelector";

// --- GraphQL Definitions ---
// Moved outside the component to prevent re-creation on every render.
const CREATE_SCHOOL_CLASS = gql`
    mutation CreateSchoolClass($name: String!, $senior: Boolean!, $teacherId: String!) {
        createSchoolClass(name: $name, senior: $senior, teacherId: $teacherId) {
            id
            name
        }
    }
`;

const GET_ALL_SCHOOL_CLASSES = gql`
    query GetAllSchoolClasses {
        allSchoolClasses {
            id
            name
        }
    }
`;

export function CreateClass() {

    // --- State Management ---
    const [className, setClassName] = useState('');
    const [classType, setClassType] = useState('senior');
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const [continueEnabled, setContinueEnabled] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // --- Hooks ---
    const navigate = useNavigate();
    const [createSchoolClass] = useMutation(CREATE_SCHOOL_CLASS);
    const { data: allClassesData } = useQuery(GET_ALL_SCHOOL_CLASSES);
    const allClasses = allClassesData?.allSchoolClasses || [];

    // --- Validation Effect ---
    useEffect(() => {
        let error = '';
        if (!className.trim()) {
            error = 'Klassenname ist erforderlich.';
        } else if (allClasses.some(cls => cls.name === className.trim())) {
            error = 'Klassenname muss eindeutig sein.';
        } else if (!selectedTeacher) {
            error = 'Bitte wähle einen Lehrer aus.';
        }

        setErrorMessage(error);
        setContinueEnabled(!error);
    }, [className, selectedTeacher, allClasses]);

    // --- Event Handlers ---
    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!continueEnabled) return;

        try {
            const response = await createSchoolClass({
                variables: {
                    name: className.trim(),
                    senior: classType === "senior",
                    teacherId: selectedTeacher,
                },
                refetchQueries: [{ query: GET_ALL_SCHOOL_CLASSES }],
                awaitRefetchQueries: true,
            });

            if (response && !response.errors) {
                navigate("/admin/classes");
            }
        } catch (err) {
            setErrorMessage(`Fehler beim Erstellen: ${err.message}`);
        }
    };

    return (
        <div className="py-4">
            <div className="max-w-2xl">
                <h1 className="text-2xl font-bold text-gray-900">Create New Class</h1>
                <p className="mt-2 text-sm text-gray-600">
                    Fill in the details below to create a new school class.
                </p>

                <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                    <TextInput
                        id="className"
                        labelText="Class Name"
                        placeholder="e.g., 10A"
                        autoComplete="off"
                        value={className}
                        onChange={(e) => setClassName(e.target.value)}
                        required
                    />
                    
                    <Select
                        id="classType"
                        labelText="Class Type"
                        value={classType}
                        onChange={(e) => setClassType(e.target.value)}
                    >
                        <SelectItem value="senior" text="Oberstufe" />
                        <SelectItem value="junior" text="Unterstufe" />
                    </Select>
                    
                    <TeacherSelector
                        onTeacherChange={setSelectedTeacher}
                        selectedTeacher={selectedTeacher}
                    />

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4">
                        <p className="text-sm text-red-600 h-5">
                            {errorMessage}
                        </p>
                        <button
                            type="submit"
                            className={`mainBtn w-full sm:w-auto text-white px-6 py-2 transition-opacity duration-200 ${
                                continueEnabled ? 'hover:bg-blue-700' : 'opacity-50 cursor-not-allowed'
                            }`}
                            disabled={!continueEnabled}
                        >
                            Klasse erstellen
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}